/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import ko from "knockout";
import birdata from "../api.js";
import moment from "dayjs";
import util from "util/util.js";

import '../util/custom-bindings.js';
import '../util/knockout-pikaday.js';
import '../ui/shared/media-uploader.js';

// Custom binding to support <select> with <optgroup>
// http://stackoverflow.com/questions/11189660/knockoutjs-binding-value-of-select-with-optgroup-and-javascript-objects
ko.bindingHandlers.option = {
    update: function(element, valueAccessor) {
       var value = ko.utils.unwrapObservable(valueAccessor());
       ko.selectExtensions.writeValue(element, value);
    }
};

var SurveyDetailsViewModel = function(params) {
	var self = this;

  self.survey = params.parent.survey;
  self.type = self.survey.type;
  self.showNextTab = params.parent.showNextTab;

  // Alias for convenience
  var survey = self.survey;
  var program = survey.program;

  self.allTypeOptions = ko.observableArray([]);
  birdata.lookup("survey_type").then(self.allTypeOptions);

  self.programTypeOptions = ko.pureComputed(function() {
    return program()?.surveyTypeOptions;
  });

  self.typeOptions = ko.pureComputed(function() {
    return self.programTypeOptions() ?? categoriseTypeOptions(self.allTypeOptions());
  });

  self.forcePrivate = ko.pureComputed(function() {
    let forcePrivate = program()?.forcePrivate;
    if(typeof forcePrivate === "function") {
      forcePrivate = forcePrivate(self.survey);
    }
    return forcePrivate;
  });

  self.waterLevelOptions = ko.observableArray([]);
  birdata.lookup("water_level").then(self.waterLevelOptions);

  // Logic to categorise survey types
  function categoriseTypeOptions(items) {
    var options = [
      {
        label: "Systematic bird survey (preferred option)",
        children: []
      },
      {
        label: "Embedded survey (two surveys in one)",
        children: [
          {
            name: "2 ha, 20 min + 500m area search",
            code: "2_ha_search+area_search_within_500_m"
          }
        ]
      },
      {
        label: "Unstandardised bird survey (useful in certain applications)",
        children: []
      },
      {
        label: "Bird list (recreational value only)",
        children: []
      }
    ];

    items.forEach(function(item) {
      switch(item.code) {
        case "2_ha_search":
        case "area_search_within_500_m":
        case "area_search_within_5_km":
        case "5_min_point_search":
          options[0].children.push(item);
          break;
        case "incidental_search":
        case "fixed_route_search":
          options[2].children.push(item);
          break;
        case "bird_list":
          options[3].children.push(item);
          break;
      }
    });

    // Remove Embedded survey option for existing surveys
    if(survey.existing()) {
      options.splice(1,1);
    }

    return options;
  }

  self.startTimeLabel = ko.pureComputed(function() {
    var p = program();
    if(p && p.startTimeLabel) {
      return p.startTimeLabel(survey);
    } else {
      return "Start time (24hr)";
    }
  });

  self.durationLabel = ko.pureComputed(function() {
    if(survey.isEmbedded()) {
      return 'Total search duration';
    } else if(survey.multipleDays()) {
      return 'Time spent surveying';
    } else {
      return 'Survey duration';
    }
  });

  self.showDurationTooLongWarning = ko.pureComputed(function() {
    return self.durationEnabled() && survey.durationInMinutes() > 70 * 60;
  });

  self.multipleDaysEnabled = ko.pureComputed(function() {
    if(survey.type() && survey.type().code.match(/2_ha_search/)) {
      return false;
    }

    var p = program();
    if(p && p.multipleDaysEnabled && p.multipleDaysEnabled(survey) === false) {
      return false;
    }

    return true;
  });


  // Allow program logic to disable duration field
  self.durationEnabled = ko.pureComputed(function() {
    return program()?.durationEnabled?.(survey) ?? true;
  });

  // Allow program logic to enforce a fixed number of survey days
  self.fixedNumberOfDays = ko.pureComputed(function() {
    return program()?.fixedNumberOfDays?.(survey) ?? 0;
  });

  self.allSpeciesRecordedEnabled = ko.pureComputed(function() {
    var p = program();
    if(p && p.allSpeciesRecordedEnabled) {
      return p.allSpeciesRecordedEnabled(survey);
    } else {
      return true;
    }
  });

  self.observerCountEnabled = ko.pureComputed(function() {
    return program()?.observerCountEnabled?.(survey) ?? true;
  });

  // --- Assign owner

  self.me = ko.observable(null);
  params.parent.mePromise.then(self.me);

  function isAdmin(user) {
    return user && (user.is_administrator || user.permissions?.some(p => p.role === "admin" ));
  }
  self.showAssignOwner = ko.pureComputed(function() {
    if(!isAdmin(self.me()) || survey.existing()) {
      return false;
    }
    var p = program();
    if(p && p.canAssignSurveyOwner !== undefined) {
      return p.canAssignSurveyOwner;
    } else {
      return true;
    }
  });

  self.usersMatchOrder = function(option, text, search) {
    if(option.new) {
      // Always put new user option at beginning of result list
      return 0;
    } else {
      var x = text && search && text.toLowerCase().indexOf(search.toLowerCase());
      if(x && x >= 0) {
        x++;
      }
      return x;
    }
  }
  self.usersMatchingSearch = ko.observable([]);
  self.usersSearchText = ko.observable('').extend({ rateLimit: 400 });

  self.usersSearchText.subscribe(function(search) {
    updateUsers(search, self.usersSearchText, self.usersMatchingSearch);
  });
  function updateUsers(search, searchObs, matchesObs) {
    if(search) {
      var newUserOption = {
        new: true,
        full_name: search
      }
      birdata.users({q:search, full: true, limit: 50}).then(function(users) {
        if(searchObs() != search) return; // ignore stale request
        users = users.filter(u => u.full_name);
        matchesObs([newUserOption].concat(users));
      });
    } else {
      matchesObs([]);
    }
  }

  self.userOptionText = function(user) {
    if(user.new) {
      return "New User: " + user.full_name;
    } else {
      var text = user.full_name || "";
      if(user.email) {
        text = text + " <" + user.email + ">";
      }
      return text;
    }
  };

  // --- End assign owner

  survey.startDate.subscribe(function() { dateChanged(true); });
  survey.finishDate.subscribe(function() { dateChanged(false); });

  function dateChanged(startChanged) {
    var days = self.fixedNumberOfDays();
    if(days) {
      var start = survey.startDate();
      var finish = survey.finishDate();
      var expectedStart = moment(start);
      var expectedFinish = moment(finish);

      if(startChanged) {
        expectedFinish = moment(start).add(days, 'day');
      } else {
        expectedStart = moment(finish).add(-days, 'day');
      }

      if(!expectedStart.isSame(start)) {
        survey.startDate(new Date(expectedStart));
      }

      if(!expectedFinish.isSame(finish)) {
        survey.finishDate(new Date(expectedFinish));
      }
    }
  }

  function enforcedDurationInMinutes(surveyTypeCode) {
    switch(surveyTypeCode) {
      case "2_ha_search": return 20;
      case "5_min_point_search": return 5;
    }
  }

  ko.computed(function() {
    if(survey.type()) {
      var duration = enforcedDurationInMinutes(survey.type().code);
      if(duration) {
        survey.durationMinutes(duration);
        survey.durationHours(0);
        survey.multipleDays(false);
      }
    }
  });
};

ko.components.register('survey-edit-details', {
	viewModel: SurveyDetailsViewModel,
	template: require('../../templates/survey-edit-details.html')
});

export default SurveyDetailsViewModel;
